import { ChatCircleIcon, GearFineIcon, WrenchIcon } from "@phosphor-icons/react"
import {
  Discount,
  DiscountKind,
  PriceUtils,
  Receipt,
  ReceiptProduct,
  hasDiscount,
  isSameDiscount,
} from "@repo/common"
import { ProductEanBadge } from "@repo/ui/components/product-ean-badge"
import { Button } from "@repo/ui/components/ui/button"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@repo/ui/components/ui/collapsible"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/ui/dialog"
import { Input } from "@repo/ui/components/ui/input"
import { cn } from "@repo/ui/lib/utils"
import {
  SelectableCardDescription,
  SelectableCardLabel,
} from "@repo/ui/tanstack-form"
import {
  Check,
  ChevronDown,
  ChevronUp,
  Minus,
  Plus,
  Trash2,
} from "lucide-react"
import React, { useState } from "react"
import { useTranslations } from "use-intl"
import { DISCOUNTED_PRICE_CLASS, OriginalPrice } from "./original-price"

export const NumProductsCircle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex justify-center items-center bg-info-foreground rounded-full size-4 pr-[0.75px] pb-[0.75px]",
      className,
    )}
    {...props}
  />
))
NumProductsCircle.displayName = "NumProductsCircle"

export const NumProductsCircleText = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <SelectableCardLabel
    ref={ref}
    className={cn("text-background text-xs", className)}
    {...props}
  />
))
NumProductsCircleText.displayName = "NumProductsCircleText"

/**
 * Renders a mechanic's comment for an action as a left-aligned row with a
 * comment icon. Capitalizes the first letter so comments read like proper
 * sentences regardless of how the mechanic typed them.
 */
export const ActionComment = ({ comment }: { comment: string | undefined }) => {
  if (!comment) return null
  const display = comment.charAt(0).toUpperCase() + comment.slice(1)
  return (
    <div className="flex items-start gap-1.5 pb-1">
      <ChatCircleIcon
        weight="regular"
        className="size-4 text-muted-foreground shrink-0 mt-0.5"
      />
      <SelectableCardDescription className="text-muted-foreground italic text-left">
        {display}
      </SelectableCardDescription>
    </div>
  )
}

interface ProductListProps {
  products: ReceiptProduct[]
  isEditable?: boolean
  onProductPriceChange?: (productIndex: number, price: number) => void
  onDeleteProduct?: (productId: string) => void
  showDeleteButton?: boolean
  /**
   * Render an EAN badge next to each product whose `ean` is non-empty. Used by
   * the CRM only — customer-facing receipts in the marketplace leave this off.
   */
  showEan?: boolean
  /**
   * Render each product as a new addition: a leading `+` icon and the
   * primary colour, matching how new actions are surfaced in the addon-order
   * summary. Off for existing/accepted products.
   */
  isProposed?: boolean
  /**
   * Render each product in the "already sent" blue (`info-foreground`) colour,
   * matching the sent action it belongs to. Unlike `isProposed` it adds no
   * leading `+` per product — the parent action row already carries the blue
   * `+`, so the products stay as `⚙️ product name` rows.
   */
  isSent?: boolean
  /**
   * Keep the leading `+` indicator's column for alignment but render it
   * invisible (`opacity-0`). Used for addon actions nested inside a package,
   * where the parent action already carries the `+` and a per-product repeat
   * is visual noise.
   */
  dimProposedIndicator?: boolean
}

export const ProductList = ({
  products,
  isEditable = false,
  onProductPriceChange,
  onDeleteProduct,
  showDeleteButton = false,
  showEan = false,
  isProposed = false,
  isSent = false,
  dimProposedIndicator = false,
}: ProductListProps) => {
  const t = useTranslations("ui")
  // Sent products share the proposed-product layout but render blue instead of
  // the primary colour, and black for plain existing products.
  const productColor = {
    "text-primary": isProposed,
    "text-info-foreground": isSent,
    "text-foreground": !isProposed && !isSent,
  }
  const [deleteDialogProductId, setDeleteDialogProductId] = useState<
    string | null
  >(null)

  if (products.length === 0) return null

  return (
    <div>
      {products.map((product, pIndex) => {
        const showDeleteDialog = deleteDialogProductId === product.id
        const quantity = product.quantity ?? 1
        const productDiscount = hasDiscount(product.discount)
          ? product.discount
          : null
        const originalTotal = product.price * quantity
        const discountedTotal = PriceUtils.applyDiscount(
          originalTotal,
          productDiscount,
        )

        return (
          <div
            key={`${product.id}-${pIndex}`}
            className="flex justify-between items-start gap-2"
          >
            <div
              className={cn("grid items-start gap-x-1.5 min-w-0", {
                "grid-cols-[auto_auto_1fr]": isProposed,
                "grid-cols-[auto_1fr]": !isProposed,
              })}
            >
              {isProposed && (
                <Plus
                  className={cn("size-4 text-primary mt-1.5 shrink-0", {
                    "opacity-0": dimProposedIndicator,
                  })}
                />
              )}
              <GearFineIcon
                weight="fill"
                className={cn("size-4 mt-1.5", productColor)}
              />
              <p className={cn("text-sm leading-7", productColor)}>
                {product.title}
                {showEan && product.ean && (
                  <>
                    {" "}
                    <ProductEanBadge ean={product.ean} />
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {showDeleteButton && onDeleteProduct && !isEditable && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteDialogProductId(product.id)}
                    className="p-1 h-auto text-destructive-foreground hover:text-destructive-foreground hover:bg-destructive-background"
                  >
                    <Minus className="size-3" />
                  </Button>
                  <Dialog
                    open={showDeleteDialog}
                    onOpenChange={(open) =>
                      setDeleteDialogProductId(open ? product.id : null)
                    }
                  >
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>
                          {t("receipt.deleteProductTitle")}
                        </DialogTitle>
                        <DialogDescription>
                          {t("receipt.deleteProductDescription", {
                            title: product.title,
                          })}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="outline"
                          onClick={() => setDeleteDialogProductId(null)}
                        >
                          {t("common.cancel")}
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => {
                            onDeleteProduct(product.id)
                            setDeleteDialogProductId(null)
                          }}
                        >
                          {t("receipt.delete")}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </>
              )}
              {isEditable ? (
                <Input
                  type="number"
                  suffix="kr"
                  min={0}
                  value={product.price}
                  onChange={(e) =>
                    onProductPriceChange?.(pIndex, Number(e.target.value) || 0)
                  }
                  className="w-[112px] ml-auto"
                />
              ) : productDiscount ? (
                <div className="flex flex-col items-end">
                  <div className="flex items-center gap-2">
                    {quantity > 1 && (
                      <SelectableCardDescription
                        className={cn("whitespace-nowrap", productColor)}
                      >
                        {quantity} st
                      </SelectableCardDescription>
                    )}
                    <OriginalPrice>
                      {PriceUtils.formatWithoutCurrency(originalTotal)}
                    </OriginalPrice>
                    <SelectableCardDescription
                      className={cn(productColor, DISCOUNTED_PRICE_CLASS)}
                    >
                      {PriceUtils.formatWithoutCurrency(discountedTotal)}
                    </SelectableCardDescription>
                  </div>
                  <span className="text-xs text-destructive-foreground">
                    {PriceUtils.formatDiscountLabel(productDiscount)}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  {quantity > 1 && (
                    <SelectableCardDescription
                      className={cn("whitespace-nowrap", productColor)}
                    >
                      {t("receipt.quantityAtPrice", {
                        quantity,
                        price: PriceUtils.formatWithoutCurrency(product.price),
                      })}
                    </SelectableCardDescription>
                  )}
                  <SelectableCardDescription className={cn(productColor)}>
                    {PriceUtils.formatWithoutCurrency(originalTotal)}
                  </SelectableCardDescription>
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface LaborPriceRowProps {
  laborPrice: number
  isEditable?: boolean
  onLaborPriceChange?: (price: number) => void
  originalLaborPrice?: number
  discount?: Discount | null
  rightElement?: React.ReactNode
  icon?: React.ReactNode
}

export const LaborPriceRow = ({
  laborPrice,
  isEditable = false,
  onLaborPriceChange,
  originalLaborPrice,
  discount,
  rightElement,
  icon,
}: LaborPriceRowProps) => {
  const t = useTranslations("ui.common")
  const laborDiscount = hasDiscount(discount) ? discount : null

  return (
    <div className="flex justify-between items-center py-1">
      <div className="flex items-center gap-1">
        {icon}
        <SelectableCardDescription>{t("labor")}</SelectableCardDescription>
        {rightElement}
      </div>
      {isEditable ? (
        <Input
          type="number"
          suffix="kr"
          min={0}
          value={laborPrice}
          onChange={(e) => onLaborPriceChange?.(Number(e.target.value) || 0)}
          className="w-[112px] ml-auto"
        />
      ) : laborDiscount && originalLaborPrice != null ? (
        <div className="flex items-center gap-2">
          <OriginalPrice>
            {PriceUtils.formatWithoutCurrency(originalLaborPrice)}
          </OriginalPrice>
          <span className="text-xs text-destructive-foreground">
            {PriceUtils.formatDiscountLabel(laborDiscount)}
          </span>
          <SelectableCardDescription className="text-destructive-foreground">
            {PriceUtils.formatWithoutCurrency(laborPrice)}
          </SelectableCardDescription>
        </div>
      ) : (
        <SelectableCardDescription>
          {PriceUtils.formatWithoutCurrency(laborPrice)}
        </SelectableCardDescription>
      )}
    </div>
  )
}

interface DiscountInfo {
  originalLaborPrice: number
  discount: Discount | null
  // Labor after the included material is taken off, shown read-only below the
  // editable original price and discount so the package total reconciles.
  laborPriceAfterMaterial?: number
  onOriginalLaborPriceChange?: (price: number) => void
  /** The editable receipt (Storybook only) edits the discount as a percent. */
  onDiscountPercentChange?: (percent: number | null) => void
}

interface CollapsibleItemProps {
  title: string
  totalCost: number
  products: ReceiptProduct[]
  laborPrice?: number
  isCollapsed: boolean
  onToggleCollapse: () => void
  isEditable?: boolean
  onProductPriceChange?: (productIndex: number, price: number) => void
  onLaborPriceChange?: (price: number) => void
  onTotalCostChange?: (cost: number) => void
  children?: React.ReactNode
  onAddProduct?: () => void
  onDeleteProduct?: (productId: string) => void
  showAddButton?: boolean
  showDeleteButton?: boolean
  discountInfo?: DiscountInfo
  isProposed?: boolean
  isAccepted?: boolean
  isSent?: boolean
  icon?: React.ReactNode
  /**
   * Optional row rendered below the title, indented to align with the title.
   * Always visible regardless of collapsed state.
   */
  subtitle?: React.ReactNode
  /**
   * Mechanic's comment for this action. Rendered above the "Arbete" row and
   * the product list so it stays grouped with the action even when collapsed.
   */
  comment?: string
  /** Forwarded to the inner ProductList — CRM only. */
  showEan?: boolean
}

export const CollapsibleItem = ({
  title,
  totalCost,
  products,
  laborPrice,
  isCollapsed,
  onToggleCollapse,
  isEditable = false,
  onProductPriceChange,
  onLaborPriceChange,
  onTotalCostChange,
  children,
  onAddProduct,
  onDeleteProduct,
  showAddButton = false,
  showDeleteButton = false,
  discountInfo,
  isProposed = false,
  isAccepted = false,
  isSent = false,
  icon,
  subtitle,
  comment,
  showEan = false,
}: CollapsibleItemProps) => {
  const t = useTranslations("ui")
  const hasSubItems = Boolean(children)

  return (
    <div className="border-b border-border pb-1">
      <Collapsible open={!isCollapsed} onOpenChange={onToggleCollapse}>
        <div
          className={cn("flex justify-between items-center pt-2 pb-1 gap-1", {
            "items-center": products.length > 0,
          })}
        >
          <div className="flex items-center gap-2">
            {isAccepted && (
              <div className="flex justify-center items-center bg-secondary-foreground rounded-full size-4">
                <Check className="size-3 text-background" />
              </div>
            )}
            {isProposed && (
              <div className="flex justify-center items-center bg-primary rounded-full size-4">
                <Plus className="size-3 text-primary-foreground" />
              </div>
            )}
            {isSent && (
              <div className="flex justify-center items-center bg-info-foreground rounded-full size-4">
                <Plus className="size-3 text-background" />
              </div>
            )}
            {icon}
            <SelectableCardLabel
              className={cn({
                "text-primary": isProposed,
                "text-secondary-foreground": isAccepted,
                "text-info-foreground": isSent,
              })}
            >
              {title}
            </SelectableCardLabel>
          </div>

          <div className="flex items-center gap-2">
            {showAddButton && onAddProduct && !isEditable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onAddProduct}
                className="p-1 h-auto text-success-foreground hover:text-success-foreground hover:bg-success-background"
              >
                <Plus className="size-4" />
              </Button>
            )}

            {isEditable && onTotalCostChange ? (
              <Input
                type="number"
                suffix="kr"
                min={0}
                value={totalCost}
                onChange={(e) => onTotalCostChange(Number(e.target.value) || 0)}
                className="w-[112px] ml-auto"
              />
            ) : (
              <SelectableCardLabel
                className={cn({
                  "text-primary": isProposed,
                  "text-secondary-foreground": isAccepted,
                  "text-info-foreground": isSent,
                })}
              >
                {PriceUtils.formatWithoutCurrency(totalCost)}
              </SelectableCardLabel>
            )}
          </div>
        </div>

        {subtitle && <div className="pl-[28px] pb-1">{subtitle}</div>}

        <div className="pl-[28px]">
          {comment && <ActionComment comment={comment} />}
          {discountInfo && isEditable ? (
            <>
              <div className="flex justify-between items-start py-1">
                <SelectableCardDescription>
                  {t("receipt.originalLaborCost")}
                </SelectableCardDescription>
                <Input
                  type="number"
                  suffix="kr"
                  min={0}
                  value={discountInfo.originalLaborPrice}
                  onChange={(e) =>
                    discountInfo.onOriginalLaborPriceChange?.(
                      Number(e.target.value) || 0,
                    )
                  }
                  className="w-[112px] ml-auto"
                />
              </div>
              <div className="flex justify-between items-start py-1">
                <SelectableCardDescription>
                  {t("common.discount")}
                </SelectableCardDescription>
                <Input
                  type="number"
                  suffix="%"
                  min={0}
                  max={100}
                  value={
                    discountInfo.discount?.kind === DiscountKind.PERCENT
                      ? discountInfo.discount.value
                      : ""
                  }
                  placeholder="0"
                  onChange={(e) => {
                    const value = e.target.value
                    discountInfo.onDiscountPercentChange?.(
                      value ? Number(value) : null,
                    )
                  }}
                  className="w-[112px] ml-auto"
                />
              </div>
              {discountInfo.laborPriceAfterMaterial !== undefined && (
                <div className="flex justify-between items-start py-1">
                  <SelectableCardDescription>
                    {t("receipt.laborAfterMaterial")}
                  </SelectableCardDescription>
                  <SelectableCardDescription>
                    {PriceUtils.formatWithoutCurrency(
                      discountInfo.laborPriceAfterMaterial,
                    )}
                  </SelectableCardDescription>
                </div>
              )}
            </>
          ) : laborPrice !== undefined ? (
            <LaborPriceRow
              laborPrice={laborPrice}
              isEditable={isEditable}
              onLaborPriceChange={onLaborPriceChange}
              originalLaborPrice={discountInfo?.originalLaborPrice}
              discount={discountInfo?.discount}
              icon={
                <WrenchIcon weight="fill" className="size-4 text-foreground" />
              }
              rightElement={
                hasSubItems ? (
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="p-1 h-auto">
                      {isCollapsed ? (
                        <ChevronDown className="size-4" />
                      ) : (
                        <ChevronUp className="size-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                ) : undefined
              }
            />
          ) : null}

          {hasSubItems && <CollapsibleContent>{children}</CollapsibleContent>}

          <ProductList
            products={products}
            isEditable={isEditable}
            onProductPriceChange={onProductPriceChange}
            onDeleteProduct={onDeleteProduct}
            showDeleteButton={showDeleteButton}
            showEan={showEan}
            isProposed={isProposed}
            isSent={isSent}
          />
        </div>
      </Collapsible>
    </div>
  )
}

interface SimpleItemProps {
  title: string
  cost: number
  isEditable?: boolean
  onCostChange?: (cost: number) => void
  onDelete?: () => void
  isDeletable?: boolean
  onAddProduct?: () => void
  showAddButton?: boolean
  isProposed?: boolean
  isAccepted?: boolean
  isSent?: boolean
  icon?: React.ReactNode
  /**
   * Optional row rendered below the title, indented to align with the title.
   */
  subtitle?: React.ReactNode
  /** Mechanic's comment, rendered inside the same block as the action. */
  comment?: string
  /**
   * The line's own discount. When set, the original price is shown struck
   * through next to `cost`, which is the price after the discount.
   */
  discount?: {
    originalCost: number
    discount: Discount | null
  }
}

export const SimpleItem = ({
  title,
  cost,
  isEditable = false,
  onCostChange,
  onDelete,
  isDeletable = false,
  onAddProduct,
  showAddButton = false,
  isProposed = false,
  isAccepted = false,
  isSent = false,
  icon,
  subtitle,
  comment,
  discount,
}: SimpleItemProps) => (
  <div className="border-b border-border pb-1">
    <div className="flex justify-between items-center py-2 gap-1">
      <div className="flex items-center gap-2">
        {isAccepted && (
          <div className="flex justify-center items-center bg-secondary-foreground rounded-full size-4">
            <Check className="size-3 text-background" />
          </div>
        )}
        {isProposed && (
          <div className="flex justify-center items-center bg-primary rounded-full size-4">
            <Plus className="size-3 text-primary-foreground" />
          </div>
        )}
        {isSent && (
          <div className="flex justify-center items-center bg-info-foreground rounded-full size-4">
            <Plus className="size-3 text-background" />
          </div>
        )}
        {icon}
        <SelectableCardLabel
          className={cn({
            "text-primary": isProposed,
            "text-secondary-foreground": isAccepted,
            "text-info-foreground": isSent,
          })}
        >
          {title}
        </SelectableCardLabel>
      </div>
      <div className="flex items-center gap-2">
        {showAddButton && onAddProduct && !isEditable && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddProduct}
            className="p-1 h-auto text-success-foreground hover:text-success-foreground hover:bg-success-background"
          >
            <Plus className="size-4" />
          </Button>
        )}
        {isDeletable && isEditable && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="p-1 h-auto text-destructive-foreground hover:text-destructive-foreground hover:bg-destructive-background"
          >
            <Trash2 className="size-4" />
          </Button>
        )}
        {isEditable ? (
          <Input
            type="number"
            suffix="kr"
            min={0}
            value={cost}
            onChange={(e) => onCostChange?.(Number(e.target.value) || 0)}
            className="w-[112px] ml-auto"
          />
        ) : discount && hasDiscount(discount.discount) ? (
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2">
              <OriginalPrice>
                {PriceUtils.formatWithoutCurrency(discount.originalCost)}
              </OriginalPrice>
              <SelectableCardLabel
                className={cn(
                  {
                    "text-primary": isProposed,
                    "text-secondary-foreground": isAccepted,
                    "text-info-foreground": isSent,
                  },
                  DISCOUNTED_PRICE_CLASS,
                )}
              >
                {PriceUtils.formatWithoutCurrency(cost)}
              </SelectableCardLabel>
            </div>
            <span className="text-xs text-destructive-foreground">
              {PriceUtils.formatDiscountLabel(discount.discount)}
            </span>
          </div>
        ) : (
          <SelectableCardLabel
            className={cn({
              "text-primary": isProposed,
              "text-secondary-foreground": isAccepted,
              "text-info-foreground": isSent,
            })}
          >
            {PriceUtils.formatWithoutCurrency(cost)}
          </SelectableCardLabel>
        )}
      </div>
    </div>
    {subtitle && <div className="pl-[28px] pb-1">{subtitle}</div>}
    {comment && (
      <div className="pl-[28px]">
        <ActionComment comment={comment} />
      </div>
    )}
  </div>
)

interface ReceiptTotalsProps {
  totalProductsCost: number
  totalLaborPrice: number
  laborDiscount?: Discount | null
  laborCostAfterDiscount?: number | null
  productsDiscount?: Discount | null
  productsCostAfterDiscount?: number | null
}

export const ReceiptTotals = ({
  totalProductsCost,
  totalLaborPrice,
  laborDiscount,
  laborCostAfterDiscount,
  productsDiscount,
  productsCostAfterDiscount,
}: ReceiptTotalsProps) => {
  const t = useTranslations("ui")
  const activeLaborDiscount = hasDiscount(laborDiscount) ? laborDiscount : null
  const activeProductsDiscount = hasDiscount(productsDiscount)
    ? productsDiscount
    : null
  const hasBothDiscounts =
    activeLaborDiscount !== null && activeProductsDiscount !== null

  return (
    <div className="space-y-2">
      {activeLaborDiscount && activeProductsDiscount && (
        <div className="flex justify-end">
          <span className="text-xs text-destructive-foreground">
            {isSameDiscount(activeLaborDiscount, activeProductsDiscount)
              ? t("receipt.discountOnLaborAndProducts", {
                  discount: PriceUtils.formatDiscountLabel(activeLaborDiscount),
                })
              : t("receipt.discountOnLaborAndOnProducts", {
                  laborDiscount:
                    PriceUtils.formatDiscountLabel(activeLaborDiscount),
                  productsDiscount: PriceUtils.formatDiscountLabel(
                    activeProductsDiscount,
                  ),
                })}
          </span>
        </div>
      )}

      <div className="flex justify-between items-start">
        <SelectableCardDescription>
          {t("receipt.products")}
        </SelectableCardDescription>
        {activeProductsDiscount ? (
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2">
              <OriginalPrice>
                {PriceUtils.formatWithoutCurrency(totalProductsCost)}
              </OriginalPrice>
              <SelectableCardDescription className="text-destructive-foreground">
                {PriceUtils.formatWithoutCurrency(
                  productsCostAfterDiscount ?? totalProductsCost,
                )}
              </SelectableCardDescription>
            </div>
            {!hasBothDiscounts && (
              <span className="text-xs text-destructive-foreground">
                {PriceUtils.formatDiscountLabel(activeProductsDiscount)}
              </span>
            )}
          </div>
        ) : (
          <SelectableCardDescription>
            {PriceUtils.formatWithoutCurrency(totalProductsCost)}
          </SelectableCardDescription>
        )}
      </div>

      <div className="flex justify-between items-start">
        <SelectableCardDescription>
          {t("common.labor")}
        </SelectableCardDescription>
        {activeLaborDiscount ? (
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2">
              <OriginalPrice>
                {PriceUtils.formatWithoutCurrency(totalLaborPrice)}
              </OriginalPrice>
              <SelectableCardDescription className="text-destructive-foreground">
                {PriceUtils.formatWithoutCurrency(
                  laborCostAfterDiscount ?? totalLaborPrice,
                )}
              </SelectableCardDescription>
            </div>
            {!hasBothDiscounts && (
              <span className="text-xs text-destructive-foreground">
                {PriceUtils.formatDiscountLabel(activeLaborDiscount)}
              </span>
            )}
          </div>
        ) : (
          <SelectableCardDescription>
            {PriceUtils.formatWithoutCurrency(totalLaborPrice)}
          </SelectableCardDescription>
        )}
      </div>
    </div>
  )
}

interface ReceiptTotalDisplayProps {
  receipt: Receipt
}

export const ReceiptTotalDisplay = ({ receipt }: ReceiptTotalDisplayProps) => {
  const t = useTranslations("ui")
  const orderDiscount = hasDiscount(receipt.orderDiscount)
    ? receipt.orderDiscount
    : null
  const hasOrderDiscount = orderDiscount !== null
  const hasLaborOrProductsDiscount =
    receipt.laborDiscount != null || receipt.productsDiscount != null

  // Calculate the base total before order discount (after labor/products discounts)
  const totalBeforeOrderDiscount = (() => {
    if (!hasOrderDiscount) return receipt.totalCost

    const laborCost =
      receipt.laborCostAfterDiscount ??
      receipt.packages.reduce(
        (sum, pkg) => sum + PriceUtils.getPackageLaborPrice(pkg),
        0,
      ) +
        receipt.partActions.reduce((sum, pa) => sum + pa.laborPrice, 0) +
        receipt.manualActions.reduce((sum, ma) => sum + ma.laborPrice, 0) +
        (receipt.wash ? receipt.wash.price : 0) +
        receipt.fees.reduce((sum, fee) => sum + fee.price, 0)

    const productsCost =
      receipt.productsCostAfterDiscount ??
      PriceUtils.getReceiptTotalProductsCost(receipt)

    return laborCost + productsCost
  })()

  return (
    <div className="flex justify-between items-start gap-1">
      <SelectableCardLabel className="font-semibold">
        {t("common.total")}
      </SelectableCardLabel>
      {orderDiscount ? (
        <div className="flex flex-col items-end">
          <div className="flex items-center gap-2">
            <OriginalPrice>
              {PriceUtils.formatWithoutCurrency(totalBeforeOrderDiscount)}
            </OriginalPrice>
            <SelectableCardLabel className="font-semibold text-destructive-foreground">
              {PriceUtils.formatWithoutCurrency(
                receipt.totalCostAfterOrderDiscount ?? receipt.totalCost,
              )}
            </SelectableCardLabel>
          </div>
          <span className="text-xs text-destructive-foreground font-semibold">
            {t("receipt.orderDiscount", {
              discount: PriceUtils.formatDiscountLabel(orderDiscount),
            })}
          </span>
        </div>
      ) : hasLaborOrProductsDiscount ? (
        <SelectableCardLabel className="font-semibold">
          {PriceUtils.formatWithoutCurrency(
            receipt.totalCostAfterOrderDiscount ?? receipt.totalCost,
          )}
        </SelectableCardLabel>
      ) : (
        <SelectableCardLabel className="font-semibold">
          {PriceUtils.formatWithoutCurrency(receipt.totalCost)}
        </SelectableCardLabel>
      )}
    </div>
  )
}
